/**
 * Typed access to Vite's `import.meta.env`. Every variable read by the app
 * lives here so that:
 *  - missing values fall back to sensible defaults,
 *  - the rest of the code never reaches into `import.meta.env` directly,
 *  - adding a new VITE_… variable is a one-line change here.
 *
 * LGPD Block E — Sentry / GA / Google OAuth readiness vars are added here,
 * ALL disabled by default. Nothing activates unless explicitly set, and GA
 * additionally requires analytics consent (enforced in the analytics wrapper).
 */

type ImportMetaEnv = {
  readonly VITE_RAGER_API_BASE_URL?: string
  readonly VITE_RAGER_APP_NAME?: string
  // ── Google Analytics (Block E) ──────────────────────────────────────────
  readonly VITE_GA_ENABLED?: string
  readonly VITE_GA_MEASUREMENT_ID?: string
  // ── Sentry (Block E) ────────────────────────────────────────────────────
  readonly VITE_SENTRY_ENABLED?: string
  readonly VITE_SENTRY_DSN?: string
  readonly VITE_SENTRY_ENVIRONMENT?: string
  readonly VITE_SENTRY_TRACES_SAMPLE_RATE?: string
  readonly VITE_SENTRY_REQUIRE_CONSENT?: string
  // ── Google OAuth placeholder (Block E) ──────────────────────────────────
  readonly VITE_GOOGLE_OAUTH_ENABLED?: string
  // ── Controlled Beta auth UX ─────────────────────────────────────────────
  readonly VITE_PUBLIC_SIGNUP_ENABLED?: string
  readonly VITE_SIGNUP_INVITE_URL?: string
  /** Injected by Vite: true under `vite dev`, false in production builds. */
  readonly DEV?: boolean
}

const env = (import.meta as unknown as { env: ImportMetaEnv }).env

function read(name: keyof ImportMetaEnv, fallback: string): string {
  const value = env?.[name]
  if (typeof value === 'string' && value.length > 0) return value
  return fallback
}

function readBool(name: keyof ImportMetaEnv, fallback: boolean): boolean {
  const value = env?.[name]
  if (typeof value !== 'string' || value.length === 0) return fallback
  return value.trim().toLowerCase() === 'true'
}

function readNumber(name: keyof ImportMetaEnv, fallback: number): number {
  const value = env?.[name]
  if (typeof value !== 'string' || value.length === 0) return fallback
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

export const APP_ENV = {
  apiBaseUrl: read('VITE_RAGER_API_BASE_URL', 'http://localhost:8010'),
  appName: read('VITE_RAGER_APP_NAME', 'Rager'),
  /**
   * Step 13.7 — true under `vite dev`, false in production builds. Used to gate
   * dev-only affordances such as the raw similarity score in source cards.
   */
  isDev: env?.DEV === true,
} as const

/**
 * LGPD Block E — analytics/monitoring/OAuth readiness flags. ALL default to a
 * privacy-preserving state (disabled). The values here are necessary-but-not-
 * sufficient: GA still requires analytics consent at runtime, and Sentry (when
 * require-consent is on) also waits for analytics consent.
 */
export const OBSERVABILITY_ENV = {
  ga: {
    enabled: readBool('VITE_GA_ENABLED', false),
    measurementId: read('VITE_GA_MEASUREMENT_ID', ''),
  },
  sentry: {
    enabled: readBool('VITE_SENTRY_ENABLED', false),
    dsn: read('VITE_SENTRY_DSN', ''),
    environment: read('VITE_SENTRY_ENVIRONMENT', ''),
    tracesSampleRate: readNumber('VITE_SENTRY_TRACES_SAMPLE_RATE', 0),
    requireConsent: readBool('VITE_SENTRY_REQUIRE_CONSENT', true),
  },
  googleOAuth: {
    enabled: readBool('VITE_GOOGLE_OAUTH_ENABLED', false),
  },
} as const

/**
 * Controlled Beta auth UX. Public self-signup is DISABLED by default — Rager is
 * invite-only during the beta. The login screen always shows an invite-only
 * notice; when ``publicSignupEnabled`` is true it additionally surfaces a link
 * to invite instructions (``inviteUrl``). This block never builds an
 * uncontrolled public account-creation form regardless of the flag.
 */
export const AUTH_ENV = {
  publicSignupEnabled: readBool('VITE_PUBLIC_SIGNUP_ENABLED', false),
  inviteUrl: read('VITE_SIGNUP_INVITE_URL', '/legal/contato-privacidade.html'),
} as const
